/**
 * ODX magnetic horizontal track — film strip + vault magnetic sections.
 * Snap uses slide-center alignment (matches CSS scroll-snap). No drag: wheel/touch snap natively; tap navigates.
 */
(function () {
  'use strict';

  var SCROLL_PROX_DEFAULT = 140;
  var INACTIVE_FRAC = 0.6 / 0.75;
  var VIDEO_T = 0.35;
  var CONTROLS_FLASH_MS = 1000;
  /**
   * Inactive .odx-magnetic__dim element opacity (active slide uses 0).
   * Pairs with CSS background #000 on .odx-magnetic__dim.
   */
  var INACTIVE_DIM_OPACITY = '0.8';
  /** Transform rubber peek (Option A): native scroll stops at anchor; 0–N px via translateX. */
  var RUBBER_MAX_DEFAULT = 200;

  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function easeOutQuad(t) {
    return 1 - (1 - t) * (1 - t);
  }

  /** Slower, predictable recoil than native `scroll-behavior: smooth` (optional via `data-odx-magnetic-recoil-ms`). */
  function magneticEaseScrollLeft(viewport, from, to, durationMs, onDone) {
    var start = null;
    function step(ts) {
      if (start === null) start = ts;
      var p = Math.min(1, (ts - start) / durationMs);
      var e = easeOutCubic(p);
      viewport.scrollLeft = from + (to - from) * e;
      if (p < 1) {
        requestAnimationFrame(step);
      } else if (onDone) {
        onDone();
      }
    }
    requestAnimationFrame(step);
  }

  function slideSnapForbidden(slide) {
    return slide && slide.dataset.odxSnapForbidden === '1';
  }

  /** Nearest scroll-metric index among slides that are not `data-odx-snap-forbidden`. */
  function nearestAllowedScrollIndex(sl, targets, slideList) {
    if (!targets || !slideList || slideList.length === 0) return 0;
    var best = -1;
    var bestD = Infinity;
    for (var i = 0; i < slideList.length; i++) {
      if (slideSnapForbidden(slideList[i])) continue;
      var d = Math.abs(sl - targets[i]);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return best < 0 ? 0 : best;
  }

  function clamp(n, a, b) {
    return Math.max(a, Math.min(b, n));
  }

  function smoothstep(t) {
    return t * t * (3 - 2 * t);
  }

  /** scrollLeft that centers slide i in the viewport (matches scroll-snap-align: center). */
  function snapScrollCenter(slides, strip, viewport, i) {
    var n = slides.length;
    if (n === 0) return 0;
    var slide = slides[i];
    var slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
    var vpHalf = viewport.clientWidth / 2;
    var maxScroll = Math.max(0, strip.scrollWidth - viewport.clientWidth);
    return Math.round(clamp(slideCenter - vpHalf, 0, maxScroll));
  }

  /** Where a slide sits vs the scroll viewport (px) and strip offset. */
  function slideWhere(slide, viewport) {
    var vp = viewport.getBoundingClientRect();
    var r = slide.getBoundingClientRect();
    return {
      stripLeft: Math.round(slide.offsetLeft),
      stripW: Math.round(slide.offsetWidth),
      vpL: Math.round(r.left - vp.left),
      vpR: Math.round(r.right - vp.left),
      vpCx: Math.round((r.left + r.right) / 2 - vp.left),
      inView: r.right > vp.left + 1 && r.left < vp.right - 1,
    };
  }

  /** Debug: computed + layout for one slide's .odx-magnetic__dim (CSS z-index, not 3D Z). */
  function describeDimForSlide(slideEl, viewport, label) {
    var el = slideEl.querySelector('.odx-magnetic__dim');
    if (!el) return label + ' (no .odx-magnetic__dim)';
    var cs = window.getComputedStyle(el);
    var r = el.getBoundingClientRect();
    var vp = viewport.getBoundingClientRect();
    return (
      label +
      ' inlineOp=' +
      (el.style.opacity || '(css)') +
      ' computedOp=' +
      cs.opacity +
      ' z-index=' +
      cs.zIndex +
      ' wxh=' +
      Math.round(r.width) +
      'x' +
      Math.round(r.height) +
      ' vpRel_LT=' +
      Math.round(r.left - vp.left) +
      ',' +
      Math.round(r.top - vp.top) +
      ' abs_LT=' +
      Math.round(r.left) +
      ',' +
      Math.round(r.top)
    );
  }

  function nearestIndex(slides, strip, viewport, targets) {
    var sl = viewport.scrollLeft;
    if (!targets) {
      targets = [];
      var ti;
      for (ti = 0; ti < slides.length; ti++) {
        targets.push(snapScrollCenter(slides, strip, viewport, ti));
      }
    }
    var best = 0;
    var bestDist = Infinity;
    for (var i = 0; i < slides.length; i++) {
      var d = Math.abs(sl - targets[i]);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    return best;
  }

  function init(root) {
    if (root.dataset.odxMagneticInit === '1') return;
    root.dataset.odxMagneticInit = '1';

    var debugPanel = root.querySelector('[data-odx-magnetic-debug-panel]');
    if (
      !debugPanel &&
      (root.getAttribute('data-odx-magnetic-debug') === '1' ||
        root.getAttribute('data-odx-magnetic-debug') === 'always')
    ) {
      var hudWrap = document.createElement('div');
      hudWrap.className = 'odx-g page-width';
      var panelShell = document.createElement('div');
      panelShell.className = 'odx-debug-panel odx-magnetic-debug-panel';
      panelShell.setAttribute('aria-hidden', 'true');
      var panelBody = document.createElement('div');
      panelBody.className = 'odx-debug-panel__body';
      debugPanel = document.createElement('pre');
      debugPanel.className = 'odx-magnetic__debug-hud';
      debugPanel.setAttribute('data-odx-magnetic-debug-panel', '');
      panelBody.appendChild(debugPanel);
      panelShell.appendChild(panelBody);
      hudWrap.appendChild(panelShell);
      var mountEarly =
        root.querySelector('.odx-cine-content-wrapper') ||
        root.querySelector('.odx-magnetic__inner') ||
        root;
      mountEarly.insertBefore(hudWrap, mountEarly.firstChild);
      try {
        panelShell.dispatchEvent(new CustomEvent('odx-debug-panel-added', { bubbles: true }));
      } catch (eHud) {}
    }

    var viewport = root.querySelector('.odx-magnetic__viewport');
    var strip = root.querySelector('.odx-magnetic__strip');
    if (!viewport || !strip) {
      if (debugPanel) debugPanel.textContent = 'odx-magnetic: missing viewport/strip';
      return;
    }

    var slides = [].slice.call(strip.querySelectorAll('.odx-magnetic__slide'));
    if (slides.length === 0) {
      if (debugPanel) debugPanel.textContent = 'odx-magnetic: 0 slides';
      return;
    }

    var reducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var controlsCurrentOnly = root.dataset.odxControlsCurrentOnly === '1';
    var controlsEnabled = root.dataset.odxControlsEnabled !== '0';
    /** Vault + Cinematheque: decode/play only on current slide; optional forbidden snap. */
    var strictMedia =
      root.classList.contains('odx-vault-magnetic') ||
      root.classList.contains('odx-cinematheque-magnetic');
    root.classList.toggle('odx-magnetic--single', slides.length === 1);

    var lastReleasedIdx = -1;
    for (var ri = 0; ri < slides.length; ri++) {
      if (!slideSnapForbidden(slides[ri])) lastReleasedIdx = ri;
    }
    var anyForbiddenSnap = slides.some(slideSnapForbidden);
    var anyReleasedSnap = slides.some(function (s) {
      return !slideSnapForbidden(s);
    });
    var hasForbiddenSnap = anyForbiddenSnap && anyReleasedSnap;
    /** Cinematheque: transform rubber by default; vault: data-odx-rubber-peek="1". Off: ="0". */
    var rubberPeekAttr = root.getAttribute('data-odx-rubber-peek');
    var useRubberPeek =
      hasForbiddenSnap &&
      strictMedia &&
      rubberPeekAttr !== '0' &&
      (rubberPeekAttr === '1' || root.classList.contains('odx-cinematheque-magnetic'));
    var rubberMaxPx = parseInt(String(root.dataset.odxRubberMax || String(RUBBER_MAX_DEFAULT)), 10);
    if (isNaN(rubberMaxPx) || rubberMaxPx < 40) rubberMaxPx = RUBBER_MAX_DEFAULT;
    var rubberSpringMs = parseInt(String(root.dataset.odxRubberSpringMs || '480'), 10);
    if (isNaN(rubberSpringMs) || rubberSpringMs < 120) rubberSpringMs = 480;

    function firstReleasedScrollIndex() {
      for (var fi = 0; fi < slides.length; fi++) {
        if (!slideSnapForbidden(slides[fi])) return fi;
      }
      return 0;
    }

    /**
     * Full strip padding pass (reflow). With rubber peek this must NOT run every scroll —
     * it resets pads and iterates layout; doing that inside updateProximity caused violent jitter.
     */
    function relayoutStripPadding() {
      if (!strictMedia || slides.length === 0) return;
      strip.style.paddingLeft = '0px';
      strip.style.paddingRight = '0px';
      void strip.offsetWidth;
      var vpW = viewport.clientWidth;
      var w0 = slides[0].offsetWidth;
      var wLast = slides[slides.length - 1].offsetWidth;
      var padL = Math.max(0, vpW / 2 - w0 / 2);
      var padR = Math.max(0, vpW / 2 - wLast / 2);
      if (strictMedia && hasForbiddenSnap && lastReleasedIdx >= 0 && !useRubberPeek) {
        var peekFrac = parseFloat(String(root.dataset.odxVaultForbiddenPeek || '0.4'));
        if (isNaN(peekFrac) || peekFrac < 0) peekFrac = 0.4;
        padR = Math.max(padR, peekFrac * vpW);
      }
      strip.style.paddingLeft = padL + 'px';
      strip.style.paddingRight = padR + 'px';

      if (useRubberPeek && lastReleasedIdx >= 0) {
        var iter;
        for (iter = 0; iter < 10; iter++) {
          void strip.offsetWidth;
          var msAlign = Math.max(0, strip.scrollWidth - viewport.clientWidth);
          var anchAlign = stripSnapTarget(lastReleasedIdx);
          if (msAlign <= anchAlign + 1.5) break;
          padR = Math.max(0, padR - (msAlign - anchAlign) * 0.88);
          strip.style.paddingRight = padR + 'px';
        }
      }
    }

    /** Vault / non-rubber: keep recomputing edge pads with proximity (slide widths stable). Rubber: skip — use relayoutStripPadding on mount/resize only. */
    function updateStripEdgePadding() {
      if (!strictMedia || slides.length === 0) return;
      if (useRubberPeek) return;
      relayoutStripPadding();
    }

    /**
     * Snap scroll positions: last released slide is biased right so a sliver of the next
     * (forbidden / unreleased) slide stays visible; pairs with scroll-padding on cinematheque + strip padR.
     */
    function stripSnapTarget(i) {
      var base = snapScrollCenter(slides, strip, viewport, i);
      var maxScroll = Math.max(0, strip.scrollWidth - viewport.clientWidth);
      if (!strictMedia || !hasForbiddenSnap || lastReleasedIdx < 0) {
        return Math.round(clamp(base, 0, maxScroll));
      }
      var isCine = root.classList.contains('odx-cinematheque-magnetic');
      if (!isCine) {
        return Math.round(clamp(base, 0, maxScroll));
      }
      var st = window.getComputedStyle(viewport);
      var rawPad = st.scrollPaddingInlineEnd || st.scrollPaddingRight || '0';
      var m = String(rawPad).match(/^([\d.]+)px/);
      var endPadPx = m ? parseFloat(m[1], 10) : 0;
      var snapBias = endPadPx > 0 ? endPadPx * 0.5 : 0;
      var lastExtra = 0;
      if (i === lastReleasedIdx) {
        var pr = root.dataset.odxForbiddenPeekReveal;
        var ex = pr != null && String(pr).trim() !== '' ? parseInt(String(pr), 10) : NaN;
        if (isNaN(ex) || ex < 0) {
          ex = Math.round(Math.min(92, Math.max(48, viewport.clientWidth * 0.082)));
        }
        lastExtra = ex;
      }
      return Math.round(clamp(base + snapBias + lastExtra, 0, maxScroll));
    }

    function buildScrollTargets() {
      var out = [];
      var j;
      for (j = 0; j < slides.length; j++) {
        out.push(stripSnapTarget(j));
      }
      return out;
    }

    var scrollProx = parseFloat(String(root.dataset.odxMagneticScrollProx || ''));
    if (isNaN(scrollProx) || scrollProx < 20) scrollProx = SCROLL_PROX_DEFAULT;

    var recoilMs = parseInt(String(root.dataset.odxMagneticRecoilMs || '0'), 10);
    if (isNaN(recoilMs) || recoilMs < 0) recoilMs = 0;
    var teaseDeferMs = parseInt(String(root.dataset.odxMagneticTeaseDefer || '140'), 10);
    if (isNaN(teaseDeferMs) || teaseDeferMs < 60) teaseDeferMs = 140;

    var rubberPx = 0;
    var rubberAnimRaf = null;
    var rubberLastClientX = 0;
    var rubberClampScrollRaf = null;

    function cancelRubberSpringAnim() {
      if (rubberAnimRaf) {
        cancelAnimationFrame(rubberAnimRaf);
        rubberAnimRaf = null;
      }
    }

    function applyStripRubberTransform() {
      if (!useRubberPeek) return;
      strip.style.transform = rubberPx > 0.25 ? 'translate3d(' + (-rubberPx) + 'px,0,0)' : '';
      strip.classList.toggle('odx-magnetic__strip--rubber', rubberPx > 0.25);
    }

    function peekAnchorScroll() {
      if (!useRubberPeek || lastReleasedIdx < 0) return 0;
      return stripSnapTarget(lastReleasedIdx);
    }

    function addRubberStepFromDelta(clientDxLeftPull) {
      if (!useRubberPeek || clientDxLeftPull === 0) return;
      var r = rubberPx / rubberMaxPx;
      var resist = Math.pow(Math.max(0, 1 - r), 2.35);
      var step = clientDxLeftPull * 0.26 * resist;
      if (step > 0 && r > 0.94) step = Math.min(step, 1.1);
      step = clamp(step, -14, 14);
      rubberPx = clamp(rubberPx + step, 0, rubberMaxPx);
      applyStripRubberTransform();
    }

    function springRubberToZero(onDone) {
      cancelRubberSpringAnim();
      if (rubberPx < 0.25) {
        rubberPx = 0;
        applyStripRubberTransform();
        if (onDone) onDone();
        return;
      }
      if (reducedMotion) {
        rubberPx = 0;
        applyStripRubberTransform();
        if (onDone) onDone();
        return;
      }
      var from = rubberPx;
      var t0 = null;
      function tick(ts) {
        if (t0 === null) t0 = ts;
        var p = Math.min(1, (ts - t0) / rubberSpringMs);
        var e = easeOutQuad(p);
        rubberPx = from * (1 - e);
        applyStripRubberTransform();
        scheduleUpdate();
        if (p < 1) {
          rubberAnimRaf = requestAnimationFrame(tick);
        } else {
          rubberPx = 0;
          applyStripRubberTransform();
          rubberAnimRaf = null;
          if (onDone) onDone();
        }
      }
      rubberAnimRaf = requestAnimationFrame(tick);
    }

    var raf = null;
    var controlsTimer = null;

    function wakeControls(slide) {
      if (!controlsEnabled || !slide) return;
      slide.classList.add('odx-controls-active');
      if (controlsTimer) clearTimeout(controlsTimer);
      controlsTimer = setTimeout(function () {
        slides.forEach(function (s) {
          s.classList.remove('odx-controls-active');
        });
      }, CONTROLS_FLASH_MS);
    }

    function setPaused(slide, paused) {
      if (!slide) return;
      if (paused) {
        slide.classList.add('is-paused');
        slide.dataset.odxPaused = '1';
      } else {
        slide.classList.remove('is-paused');
        slide.dataset.odxPaused = '0';
      }
    }

    function slidePlayable(slide) {
      return slide && slide.dataset.odxSlidePlayable !== '0';
    }

    function clearVideoSource(vid) {
      if (!vid) return;
      if (vid.getAttribute('src')) {
        vid.removeAttribute('src');
        try {
          vid.load();
        } catch (e) {}
      }
    }

    function updateProximity() {
      if (strictMedia) {
        updateStripEdgePadding();
      }

      var sl = viewport.scrollLeft;
      var proximitySl = useRubberPeek ? sl + rubberPx : sl;
      var targets = buildScrollTargets();
      var snapIndexRaw = nearestIndex(slides, strip, viewport, targets);
      var snapIndex =
        strictMedia && hasForbiddenSnap
          ? nearestAllowedScrollIndex(sl, targets, slides)
          : snapIndexRaw;

      var ts = [];
      var i;
      for (i = 0; i < slides.length; i++) {
        var distScroll = Math.abs(proximitySl - targets[i]);
        var raw = 1 - clamp(distScroll / scrollProx, 0, 1);
        ts.push(smoothstep(raw));
      }

      var maxT = ts.length ? Math.max.apply(null, ts) : 0;
      var maxIdx = 0;
      for (i = 1; i < ts.length; i++) {
        if (ts[i] > ts[maxIdx]) maxIdx = i;
      }

      var dimOpacityByIdx = [];

      slides.forEach(function (slide, idx) {
        var isSnap = idx === snapIndex;
        var t = ts[idx];
        var scale = isSnap ? 1 : INACTIVE_FRAC;
        var inner = slide.querySelector('.odx-magnetic__frame-inner');
        if (inner) {
          inner.style.transform = reducedMotion ? '' : 'scale(' + scale + ')';
        }

        var dim = slide.querySelector('.odx-magnetic__dim');
        var dimOp = isSnap ? '0' : INACTIVE_DIM_OPACITY;
        dimOpacityByIdx.push(dimOp);
        if (dim) {
          dim.style.opacity = dimOp;
        }

        slide.classList.toggle('is-current', isSnap);

        var vid = slide.querySelector('video.odx-magnetic__video');
        if (vid) {
          var ds = vid.getAttribute('data-src');
          var isWinner = idx === snapIndex;
          var isPausedByUser = slide.dataset.odxPaused === '1';
          var playable = slidePlayable(slide);

          if (strictMedia) {
            if (!playable || !ds) {
              vid.pause();
              clearVideoSource(vid);
            } else if (isWinner) {
              if (!vid.getAttribute('src')) {
                vid.setAttribute('src', ds);
                vid.load();
              }
              if (!isPausedByUser) {
                vid.play().catch(function () {});
                setPaused(slide, false);
              } else {
                vid.pause();
                setPaused(slide, true);
              }
            } else {
              vid.pause();
              clearVideoSource(vid);
            }
          } else if (ds && (t > VIDEO_T || (isWinner && maxT > 0.15))) {
            if (!vid.getAttribute('src')) {
              vid.setAttribute('src', ds);
              vid.load();
            }
            if ((isWinner && !isPausedByUser) || (!isWinner && t > 0.55)) {
              vid.play().catch(function () {});
              if (isWinner) setPaused(slide, false);
            } else {
              vid.pause();
            }
          } else {
            vid.pause();
          }
        }
      });

      if (!strictMedia && slides.length > 1) {
        var waitingIdx = clamp(snapIndex + 1, 0, slides.length - 1);
        var waitingSlide = slides[waitingIdx];
        var waitingVid = waitingSlide.querySelector('video.odx-magnetic__video');
        if (waitingVid && slidePlayable(waitingSlide)) {
          var waitingSrc = waitingVid.getAttribute('data-src');
          if (waitingSrc && !waitingVid.getAttribute('src')) {
            waitingVid.setAttribute('src', waitingSrc);
            waitingVid.load();
          }
          waitingVid.pause();
        }
      }

      if (debugPanel) {
        var maxScroll = Math.max(0, strip.scrollWidth - viewport.clientWidth);
        var n = slides.length;
        var prevIdx = n > 0 ? (snapIndex - 1 + n) % n : 0;
        var nextIdx = n > 0 ? (snapIndex + 1) % n : 0;
        var prevW = n > 0 ? slideWhere(slides[prevIdx], viewport) : null;
        var nextW = n > 0 ? slideWhere(slides[nextIdx], viewport) : null;
        var curW = n > 0 ? slideWhere(slides[snapIndex], viewport) : null;

        var rubberLines = [];
        if (useRubberPeek) {
          var apDbg = lastReleasedIdx >= 0 ? stripSnapTarget(lastReleasedIdx) : -1;
          var padRCs = window.getComputedStyle(strip).paddingRight;
          rubberLines = [
            '--- rubber peek ---',
            'useRubberPeek=1 rubberPx=' +
              rubberPx.toFixed(1) +
              '/' +
              rubberMaxPx +
              ' springMs=' +
              rubberSpringMs,
            'peekAnchor(lastReleased)=' + apDbg.toFixed(1) + ' lastReleasedIdx=' + lastReleasedIdx,
            'proximitySl(scrollLeft+rubber)=' + proximitySl.toFixed(1),
            'pointersDown=' + peekPointerIds.size + ' springAnim=' + (rubberAnimRaf ? '1' : '0'),
            'strip padR=' + padRCs + ' transform=' + (strip.style.transform || '(css)'),
          ];
        } else if (hasForbiddenSnap && strictMedia) {
          rubberLines = ['--- rubber peek ---', 'useRubberPeek=0 (native pad / recoil)'];
        }

        var lines = [
          'odx-magnetic debug',
          'scrollLeft=' + sl.toFixed(1) + ' maxScroll=' + maxScroll.toFixed(1),
          'scrollProx(px)=' + scrollProx + ' viewportW=' + viewport.clientWidth,
          'snapIndex(authority)=' +
            snapIndex +
            (strictMedia && hasForbiddenSnap ? ' raw=' + snapIndexRaw : ''),
          'snapTargets=[' + targets.map(function (x) { return x.toFixed(0); }).join(', ') + ']',
          't(blend)=[' + ts.map(function (x) { return x.toFixed(2); }).join(', ') + '] maxIdx=' + maxIdx,
          'dimOpacity[]=' + dimOpacityByIdx.join(','),
          'INACTIVE_DIM_OPACITY(js)=' + INACTIVE_DIM_OPACITY + ' (active=0)',
          n > 0
            ? 'current i=' +
              snapIndex +
              ' dimOp=' +
              dimOpacityByIdx[snapIndex] +
              ' strip@x=' +
              curW.stripLeft +
              'w' +
              curW.stripW +
              ' vp[L=' +
              curW.vpL +
              ' Cx=' +
              curW.vpCx +
              ' R=' +
              curW.vpR +
              '] inView=' +
              curW.inView
            : '',
          n > 1
            ? 'prev i=' +
              prevIdx +
              ' dimOp=' +
              dimOpacityByIdx[prevIdx] +
              ' strip@x=' +
              prevW.stripLeft +
              ' vp[L=' +
              prevW.vpL +
              ' R=' +
              prevW.vpR +
              '] inView=' +
              prevW.inView
            : '',
          n > 1
            ? 'next i=' +
              nextIdx +
              ' dimOp=' +
              dimOpacityByIdx[nextIdx] +
              ' strip@x=' +
              nextW.stripLeft +
              ' vp[L=' +
              nextW.vpL +
              ' R=' +
              nextW.vpR +
              '] inView=' +
              nextW.inView
            : '',
          n > 1 ? describeDimForSlide(slides[1], viewport, 'slide[1] (2nd) dim') : '',
          'reducedMotion=' + reducedMotion,
        ].concat(rubberLines);
        debugPanel.textContent = lines
          .filter(function (s) {
            return s !== '';
          })
          .join('\n');
      }
    }

    function scheduleUpdate() {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        raf = null;
        updateProximity();
      });
    }

    function snapToIndex(index, smooth) {
      if (useRubberPeek) {
        cancelRubberSpringAnim();
        rubberPx = 0;
        applyStripRubberTransform();
      }
      index = clamp(index, 0, slides.length - 1);
      if (strictMedia && hasForbiddenSnap && index > lastReleasedIdx) {
        index = lastReleasedIdx;
      }
      var target = stripSnapTarget(index);
      var useSmooth = smooth && !reducedMotion;
      viewport.scrollTo({
        left: target,
        behavior: useSmooth ? 'smooth' : 'auto',
      });
    }

    var peekScrollTimer = null;
    /** Active pointers on the viewport — forbidden-peek recoil runs only after all are released. */
    var peekPointerIds = new Set();

    function cancelPeekRecoilTimer() {
      if (peekScrollTimer) {
        clearTimeout(peekScrollTimer);
        peekScrollTimer = null;
      }
    }

    function recoilForbiddenPeekThenUpdate() {
      if (useRubberPeek && rubberPx > 0.5) {
        springRubberToZero(scheduleUpdate);
        return;
      }
      if (strictMedia && hasForbiddenSnap && lastReleasedIdx >= 0) {
        var centerLast = stripSnapTarget(lastReleasedIdx);
        var slNow = viewport.scrollLeft;
        if (slNow > centerLast + 0.5) {
          if (recoilMs >= 80 && !reducedMotion) {
            magneticEaseScrollLeft(viewport, slNow, centerLast, recoilMs, scheduleUpdate);
            return;
          }
          viewport.scrollTo({
            left: centerLast,
            behavior: reducedMotion ? 'auto' : 'smooth',
          });
        }
      }
      scheduleUpdate();
    }

    viewport.addEventListener(
      'scroll',
      function () {
        if (useRubberPeek && lastReleasedIdx >= 0) {
          if (!rubberClampScrollRaf) {
            rubberClampScrollRaf = requestAnimationFrame(function () {
              rubberClampScrollRaf = null;
              var apC = stripSnapTarget(lastReleasedIdx);
              if (viewport.scrollLeft > apC + 0.5) {
                viewport.scrollLeft = apC;
              }
            });
          }
        }
        scheduleUpdate();
      },
      { passive: true },
    );
    /**
     * Escape hatch: map vertical wheel intent to horizontal movement only while
     * there is horizontal runway left. At either edge, release the wheel event
     * so page-level vertical scrolling continues immediately.
     */
    viewport.addEventListener(
      'wheel',
      function (ev) {
        if (ev.ctrlKey) return;
        if (Math.abs(ev.deltaY) <= Math.abs(ev.deltaX)) return;

        var maxScroll = Math.max(0, strip.scrollWidth - viewport.clientWidth);
        if (maxScroll <= 0) return;

        var sl = viewport.scrollLeft;
        var next = clamp(sl + ev.deltaY, 0, maxScroll);
        if (Math.abs(next - sl) < 0.5) {
          if (useRubberPeek && lastReleasedIdx >= 0) {
            var apW = stripSnapTarget(lastReleasedIdx);
            if (sl >= apW - 1 && ev.deltaY !== 0) {
              ev.preventDefault();
              addRubberStepFromDelta(ev.deltaY * 0.42);
              scheduleUpdate();
            }
          }
          return;
        }

        ev.preventDefault();
        viewport.scrollLeft = next;
        scheduleUpdate();
      },
      { passive: false },
    );

    function armRecoilAfterTease() {
      cancelPeekRecoilTimer();
      peekScrollTimer = setTimeout(recoilForbiddenPeekThenUpdate, teaseDeferMs);
    }

    function onRubberPointerMove(ev) {
      if (!useRubberPeek || peekPointerIds.size === 0) return;
      var ap = peekAnchorScroll();
      var dx = rubberLastClientX - ev.clientX;
      rubberLastClientX = ev.clientX;
      if (dx === 0) return;
      var sl = viewport.scrollLeft;
      if (sl < ap - 2 && rubberPx < 0.25) return;
      var towardMorePeek = dx > 0;
      if (!towardMorePeek && rubberPx < 0.25) return;
      if (sl < ap - 1 && towardMorePeek) return;
      ev.preventDefault();
      if (sl > ap + 0.25) viewport.scrollLeft = ap;
      addRubberStepFromDelta(dx);
      scheduleUpdate();
    }

    function onPeekPointerDown(ev) {
      if (!viewport.contains(ev.target)) return;
      peekPointerIds.add(ev.pointerId);
      rubberLastClientX = ev.clientX;
      if (useRubberPeek) cancelRubberSpringAnim();
      cancelPeekRecoilTimer();
    }

    function onPeekPointerUp(ev) {
      peekPointerIds.delete(ev.pointerId);
      if (peekPointerIds.size === 0) {
        armRecoilAfterTease();
      }
    }

    if (window.PointerEvent) {
      viewport.addEventListener('pointerdown', onPeekPointerDown, { passive: true });
      if (useRubberPeek) {
        viewport.addEventListener('pointermove', onRubberPointerMove, { passive: false });
      }
      window.addEventListener('pointerup', onPeekPointerUp, { passive: true });
      window.addEventListener('pointercancel', onPeekPointerUp, { passive: true });
    } else {
      viewport.addEventListener(
        'touchstart',
        function (ev) {
          var i;
          for (i = 0; i < ev.targetTouches.length; i++) {
            peekPointerIds.add('t' + ev.targetTouches[i].identifier);
          }
          cancelPeekRecoilTimer();
        },
        { passive: true },
      );
      window.addEventListener(
        'touchend',
        function (ev) {
          var j;
          for (j = 0; j < ev.changedTouches.length; j++) {
            peekPointerIds.delete('t' + ev.changedTouches[j].identifier);
          }
          if (peekPointerIds.size === 0) armRecoilAfterTease();
        },
        { passive: true },
      );
      window.addEventListener(
        'touchcancel',
        function (ev) {
          var k;
          for (k = 0; k < ev.changedTouches.length; k++) {
            peekPointerIds.delete('t' + ev.changedTouches[k].identifier);
          }
          if (peekPointerIds.size === 0) armRecoilAfterTease();
        },
        { passive: true },
      );
    }

    viewport.addEventListener('scrollend', function () {
      if (peekPointerIds.size === 0) armRecoilAfterTease();
    }, { passive: true });

    function authorityFromScrollLeft(scrollLeftPx, targets) {
      if (strictMedia && hasForbiddenSnap) {
        return nearestAllowedScrollIndex(scrollLeftPx, targets, slides);
      }
      return nearestIndex(slides, strip, viewport, targets);
    }

    slides.forEach(function (slide, idx) {
      slide.addEventListener('click', function (ev) {
        var targets = buildScrollTargets();
        var snapIndex = authorityFromScrollLeft(viewport.scrollLeft, targets);
        if (idx !== snapIndex) {
          var dest = idx;
          if (strictMedia && hasForbiddenSnap && slideSnapForbidden(slides[idx])) {
            dest = idx < firstReleasedScrollIndex() ? firstReleasedScrollIndex() : lastReleasedIdx;
          }
          snapToIndex(dest, true);
          wakeControls(slides[dest]);
          return;
        }
        if (!controlsEnabled) return;
        if (ev.target.closest('.odx-mute-btn') || ev.target.closest('.odx-fs-btn')) return;
        var activeVideo = slide.querySelector('video.odx-magnetic__video');
        if (!activeVideo) return;
        if (!slidePlayable(slide)) return;
        if (controlsCurrentOnly && idx !== snapIndex) return;
        if (activeVideo.paused) {
          activeVideo.play().catch(function () {});
          setPaused(slide, false);
        } else {
          activeVideo.pause();
          setPaused(slide, true);
        }
        wakeControls(slide);
      });

      slide.addEventListener('click', function (ev) {
        if (!controlsEnabled) return;
        var targets = buildScrollTargets();
        var snapIndex = authorityFromScrollLeft(viewport.scrollLeft, targets);
        if (idx !== snapIndex) return;
        var video = slide.querySelector('video.odx-magnetic__video');
        if (!video || !slidePlayable(slide)) return;
        var muteBtn = ev.target.closest('.odx-mute-btn');
        if (muteBtn) {
          video.muted = !video.muted;
          wakeControls(slide);
          ev.stopPropagation();
          return;
        }
        var fsBtn = ev.target.closest('.odx-fs-btn');
        if (fsBtn) {
          var frame = slide.querySelector('.odx-magnetic__frame-inner') || slide;
          if (!document.fullscreenElement && frame.requestFullscreen) {
            frame.requestFullscreen();
          } else if (document.exitFullscreen) {
            document.exitFullscreen();
          }
          wakeControls(slide);
          ev.stopPropagation();
        }
      });

      slide.addEventListener('mousemove', function () {
        var targets = buildScrollTargets();
        var snapIndex = authorityFromScrollLeft(viewport.scrollLeft, targets);
        if (idx === snapIndex) wakeControls(slide);
      });

      slide.addEventListener('touchstart', function () {
        var targets = buildScrollTargets();
        var snapIndex = authorityFromScrollLeft(viewport.scrollLeft, targets);
        if (idx === snapIndex) wakeControls(slide);
      }, { passive: true });

      slide.addEventListener(
        'pointerdown',
        function () {
          var targets = buildScrollTargets();
          var snapIndex = authorityFromScrollLeft(viewport.scrollLeft, targets);
          if (idx === snapIndex && slidePlayable(slide)) wakeControls(slide);
        },
        { passive: true },
      );
    });

    window.addEventListener(
      'resize',
      function () {
        if (useRubberPeek) {
          cancelRubberSpringAnim();
          rubberPx = 0;
          applyStripRubberTransform();
        }
        if (strictMedia) relayoutStripPadding();
        scheduleUpdate();
      },
      { passive: true },
    );

    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(function () {
        if (strictMedia) relayoutStripPadding();
        scheduleUpdate();
      });
      ro.observe(viewport);
      ro.observe(strip);
    }

    strip.querySelectorAll('img').forEach(function (img) {
      if (img.complete) return;
      img.addEventListener(
        'load',
        function () {
          if (strictMedia) relayoutStripPadding();
          scheduleUpdate();
        },
        { once: true },
      );
    });

    if (strictMedia) relayoutStripPadding();
    scheduleUpdate();
  }

  function boot() {
    document.querySelectorAll('[data-odx-magnetic]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (ev) {
    var root = ev.target;
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('[data-odx-magnetic]').forEach(init);
  });
})();
